import { useCallback } from 'react';

import { Workflows } from '../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../tracking/use-workflow-tracking';

import type { AutomationRule } from '../types/automation-rule-types';
import { computeTemplateDiff } from '../helpers/compute-template-diff';
import type { TemplateDiff } from '../helpers/compute-template-diff';

type BaseData = {
  rule?: AutomationRule;
  isAiGenerated?: boolean;
};

type StartMetadata = BaseData & {
  source?: string;
  prompt?: string;
  playbook?: string;
};

type CompleteMetadata = BaseData & {
  playbook?: string;
  templateDiff?: TemplateDiff | null;
};

type ErrorMetadata = BaseData & {
  error?: Error | string;
  prompt?: string;
};

type CancelMetadata = BaseData & {
  selectedRule?: AutomationRule;
};

type MetadataForStep = {
  START: StartMetadata;
  COMPLETE: CompleteMetadata;
  ERROR: ErrorMetadata;
  CANCEL: CancelMetadata;
};

export const AutomationRulesSteps = {
  START: WorkflowSteps.START as 'START',
  COMPLETE: WorkflowSteps.COMPLETE as 'COMPLETE',
  ERROR: WorkflowSteps.ERROR as 'ERROR',
  CANCEL: WorkflowSteps.CANCEL as 'CANCEL',
} as const;

type AutomationRulesWorkflowStep = keyof MetadataForStep;

type TrackCompleteHelperArgs = {
  rule: AutomationRule;
  selectedRule: AutomationRule;
  filterId: string | undefined;
};

export const buildCompleteMetadata = ({ rule, selectedRule, filterId }: TrackCompleteHelperArgs): CompleteMetadata => {
  const base: CompleteMetadata = {
    rule,
    isAiGenerated: Boolean(rule.isAiGenerated),
  };

  if (selectedRule.isTemplate && selectedRule.playbook) {
    base.playbook = selectedRule.playbook;
    base.templateDiff = computeTemplateDiff(selectedRule.templateSnapshot, rule, filterId);
  }

  return base;
};

export const useTrackAutomationRulesWorkflow = <S extends AutomationRulesWorkflowStep>(workflowStep: S) => {
  const track = useTrackWorkflow(Workflows.CREATE_AUTOMATION_RULES_FLOW);

  return useCallback((metadata: MetadataForStep[S] = {} as MetadataForStep[S]) => track(workflowStep, metadata), [track, workflowStep]);
};
